import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { DatePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DatePicker';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { getDropDownValuesDict } from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import CrossIcon from '@lmt/unicorn-shared/app/icons/gray-cross.svg?react';
import {
  TimeDirection,
  TimeDirections,
  TimeUnit,
  TimeUnitsSortedDescending,
  TimeUnitValues,
} from '@lmt/unicorn-shared/app/helpers/date-helper';
import { DateRangeSetting } from '@lmt/shared-types';
import { bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './DateRangeConfiguration.module.scss';

import { Input } from '@lmt/unicorn-shared/src/app/components/common/ui-kit/inputs/Input';
import { IconButton } from '@mui/material';
import { startOfToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

function toCalloutProps(
  data: DateRangeSetting | undefined | null,
): CalloutProps {
  if (!data)
    return {
      ...getClosestUnitOfTimeForUnit(0, 'days'),
      relativeToToday: false,
    };
  const convertedValue = getClosestUnitOfTime(data.rangeInSeconds);
  return {
    count: convertedValue.count,
    direction: convertedValue.direction,
    unit: convertedValue.unit,
    relativeToToday: data.isRelativeToCurrentMoment,
  };
}

function validate(data: CalloutProps) {
  return !data || !!data.direction == !!data.unit;
}

function convertToSeconds(data: CalloutProps): number {
  const { count, unit, direction } = data;
  if (!count || !unit || !direction) return 0;

  return Number(count) * TimeUnitValues[unit] * (direction == 'past' ? -1 : 1);
}

function shortFormat(time: {
  count?: number;
  unit?: TimeUnit;
  direction?: TimeDirection;
}): string {
  const { count, unit, direction } = time;
  if (!count || !unit || !direction) return '';

  return `${direction === 'past' ? '-' : '+'} ${count}${unit[0]}`;
}

type ClosestUnitOfTime = {
  count: number;
  unit: TimeUnit;
  direction: TimeDirection;
};

function getClosestUnitOfTime(seconds?: number | null): ClosestUnitOfTime {
  if (!seconds)
    return {
      count: 0,
      unit: 'days',
      direction: 'future',
    };

  const units = TimeUnitsSortedDescending;

  for (const unit of units) {
    const duration = TimeUnitValues[unit];
    if (seconds % duration == 0) {
      return getClosestUnitOfTimeForUnit(seconds, unit);
    }
  }
  return getClosestUnitOfTimeForUnit(seconds, 'days');
}

function getClosestUnitOfTimeForUnit(
  seconds: number,
  unit: TimeUnit,
): ClosestUnitOfTime {
  const duration = TimeUnitValues[unit];
  const value = seconds / duration;
  const direction = Math.sign(value) > 0 ? 'future' : 'past';
  return {
    count: Math.abs(value),
    unit: unit,
    direction: direction,
  };
}

interface Props {
  value: DateRangeSetting | null | undefined;
  onChange: (newValue: DateRangeSetting | null | undefined) => void;
  dateError?: boolean;
  rangeError?: boolean;
}

interface CalloutProps {
  count: number | string | null;
  unit: TimeUnit | null;
  direction: TimeDirection | null;
  relativeToToday: boolean;
}

export const DateRangeConfiguration: FC<Props> = React.memo(
  function DateRangeConfiguration(props) {
    const { value: formValue, onChange, dateError, rangeError } = props;
    const i18next = useScopedTranslation(
      'patients.list.filter.date_range_configuration',
    );
    const i18n = useTranslation();

    const [calloutError, setCalloutError] = useState<string | null>(null);
    const calloutRef = useRef<HTMLDivElement>(null);

    const directions = useMemo(
      () =>
        getDropDownValuesDict(
          TimeDirections,
          'patients.list.filter.date_range_configuration.directions',
        ),
      [],
    );

    const timeUnits = useMemo(
      () =>
        getDropDownValuesDict(
          TimeUnitsSortedDescending,
          'patients.list.filter.date_range_configuration.time_units',
        ),
      [],
    );

    const [calloutState, setCalloutState] = useState<CalloutProps>(
      toCalloutProps(undefined),
    );

    const submitCalloutValues = (calloutState: CalloutProps) => {
      if (validate(calloutState)) {
        const rangeInSeconds =
          !!calloutState?.direction &&
          !!calloutState?.count &&
          !!calloutState?.unit
            ? convertToSeconds(calloutState)
            : null;

        onChange({
          isRelativeToCurrentMoment: calloutState?.relativeToToday,
          rangeInSeconds: rangeInSeconds,
          absoluteMoment: !calloutState?.relativeToToday
            ? (formValue?.absoluteMoment ?? null)
            : startOfToday(),
        });

        setCalloutError(null);
        popupState.close();
      } else {
        setCalloutError(i18next.t('invalid_callout_data'));
      }
    };

    const onCalloutOpen = () => {
      setCalloutState(toCalloutProps(formValue));
      setCalloutError(null);
      popupState.open();
    };

    const onValueReset = () => {
      onChange(null);
    };

    const convertedValue = useMemo(
      () => getClosestUnitOfTime(formValue?.rangeInSeconds),
      [formValue],
    );
    const popupState = usePopupState({
      variant: 'popper',
      popupId: 'date-range-configuration',
    });

    return (
      <div className={styles.container}>
        <DatePicker
          inputClassName={styles.datePicker}
          value={formValue?.absoluteMoment ?? null}
          onDateChanged={(date) => {
            onChange({
              rangeInSeconds: null,
              ...formValue,
              absoluteMoment: date,
              isRelativeToCurrentMoment: false,
            });
          }}
          placeholder={i18next.t('date_placeholder')}
          customText={
            formValue?.isRelativeToCurrentMoment ? 'Today' : undefined
          }
          isFormInput={false}
          disableKeyboardMode={true}
          disableInternalErrors={true}
          allowToSelectYear={true}
          endAdornment={
            <>
              <div className={styles.extraText} onClick={onCalloutOpen}>
                {shortFormat({
                  count: convertedValue.count,
                  unit: convertedValue.unit,
                  direction: convertedValue.direction,
                })}
              </div>
              <IconButton className={styles.cross} onClick={onValueReset}>
                <CrossIcon />
              </IconButton>
            </>
          }
          error={!!dateError || !!rangeError}
          errorText={!!dateError ? i18n.t('errors.invalid_date') : undefined}
        />
        <div
          ref={calloutRef}
          className={styles.setPeriod}
          onClick={() => popupState.open(calloutRef.current)}
        >
          {i18next.t('set_period')}
        </div>
        <AppPopper {...bindMenu(popupState)} anchorEl={calloutRef.current}>
          <div className={styles.callout}>
            <div className={styles.row}>
              <Field title={i18next.t('direction')}>
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={directions}
                  required={true}
                  value={calloutState?.direction}
                  idFunction={'value'}
                  getOptionLabel={'label'}
                  placeholder={''}
                  onValueChanged={({ value, label }) => {
                    setCalloutState((prev) => ({
                      ...prev,
                      direction: value as TimeDirection,
                    }));
                  }}
                  autosizeInputWidth={true}
                />
              </Field>

              <Field title={i18next.t('count')}>
                <Input
                  data-form-input={false}
                  className={styles.unitCountInput}
                  value={calloutState.count ?? ''}
                  onChange={(e) => {
                    const value = e.currentTarget.value.replaceAll(/\D/g, '');

                    setCalloutState((prev) => ({ ...prev, count: value }));
                  }}
                />
              </Field>

              <Field title={i18next.t('unit')}>
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={timeUnits}
                  value={calloutState.unit}
                  idFunction={'value'}
                  getOptionLabel={'label'}
                  placeholder={''}
                  required={true}
                  onValueChanged={({ value, label }) => {
                    setCalloutState((prev) => ({
                      ...prev,
                      unit: value as TimeUnit,
                    }));
                  }}
                  autosizeInputWidth={true}
                />
              </Field>
            </div>
            <CheckBox
              className={styles.todayToggle}
              title={i18next.t('toggle_today')}
              checked={calloutState.relativeToToday}
              onChange={(e) => {
                setCalloutState((prev) => ({
                  ...prev,
                  relativeToToday: e.target.checked,
                }));
              }}
            />
            {(!!calloutError || !!rangeError) && (
              <div data-error={true} className={styles.helperText}>
                {calloutError ?? i18next.t('range_error')}
              </div>
            )}
            <div className={styles.buttonsContainer}>
              <Button
                title={i18next.t('buttons.clear_range')}
                color={ButtonColor.Primary}
                onClick={() => {
                  setCalloutState(toCalloutProps(undefined));
                  onValueReset();
                  popupState.close();
                }}
              />
              <Button
                title={i18next.t('buttons.done')}
                onClick={() => submitCalloutValues(calloutState)}
              />
            </div>
          </div>
        </AppPopper>
      </div>
    );
  },
);
